import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';

interface SkillPack {
  id: string;
  title: string;
  category: 'legal' | 'finance' | 'tech' | 'management' | 'growth';
  badge: string;
  author: string;
  price: number;
  price_label: string;
  rating: number;
  reviews_count: number;
  installs_count: number;
  description: string;
  artifacts: string[];
  installed?: boolean;
  created_at: string;
}

export function MarketplacePage() {
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState<SkillPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'catalog' | 'my_packs'>('catalog');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [installingId, setInstallingId] = useState<string | null>(null);

  // Publish Modal
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishForm, setPublishForm] = useState({
    title: '',
    category: 'tech' as SkillPack['category'],
    badge: '⚡ Kỹ Thuật',
    price: 0,
    description: '',
    artifacts: 'SOP_Kien_Truc_He_Thong.md\nPrompt_Review_Code.md\nChecklist_Deploy_Production.md',
    privacy_clean_consent: true
  });

  useEffect(() => {
    loadPacks();
  }, []);

  const loadPacks = async () => {
    setLoading(true);
    try {
      const res = await api.getMarketplacePacks();
      setPacks(res.packs || []);
    } catch (e) {
      console.error('Failed to load marketplace packs', e);
    } finally {
      setLoading(false);
    }
  };

  const handleInstall = async (pack: SkillPack) => {
    setInstallingId(pack.id);
    try {
      const res = await api.installMarketplacePack(pack.id);
      if (res.success) {
        setPacks(prev => prev.map(p => p.id === pack.id ? { ...p, installed: true, installs_count: p.installs_count + 1 } : p));
        alert(`🎉 Cài đặt thành công Skill-Pack: "${pack.title}"!\n\nToàn bộ quy trình SOP và tài nguyên đã được tự động nạp vào Operations Hub (.ai/operations/).`);
      }
    } catch (e: any) {
      alert('Lỗi khi cài đặt: ' + e.message);
    } finally {
      setInstallingId(null);
    }
  };

  const handlePublish = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!publishForm.title.trim() || !publishForm.description.trim()) {
      alert('Vui lòng điền đầy đủ tiêu đề và mô tả gói kỹ năng.');
      return;
    }
    if (!publishForm.privacy_clean_consent) {
      alert('Vui lòng đồng ý để Privacy Guard quét sạch dữ liệu trước khi đăng bán.');
      return;
    }

    setPublishing(true);
    try {
      const artifactList = publishForm.artifacts
        .split('\n')
        .map(a => a.trim())
        .filter(Boolean);

      const res = await api.createMarketplacePack({
        title: publishForm.title.trim(),
        category: publishForm.category,
        badge: publishForm.badge,
        price: Number(publishForm.price),
        description: publishForm.description.trim(),
        artifacts: artifactList.length > 0 ? artifactList : ['SOP_Chuan_Hoa_Vault.md']
      });

      if (res.success && res.pack) {
        setPacks([res.pack, ...packs]);
        setShowPublishModal(false);
        setPublishForm({
          title: '',
          category: 'tech',
          badge: '⚡ Kỹ Thuật',
          price: 0,
          description: '',
          artifacts: 'SOP_Kien_Truc_He_Thong.md\nPrompt_Review_Code.md\nChecklist_Deploy_Production.md',
          privacy_clean_consent: true
        });
        alert('🚀 Đã niêm yết Skill-Pack thành công lên Sàn Giao Dịch Tri Thức!');
      }
    } catch (e: any) {
      alert('Lỗi đăng bán: ' + e.message);
    } finally {
      setPublishing(false);
    }
  };

  const categories = [
    { id: 'all', label: 'Tất Cả Danh Mục' },
    { id: 'legal', label: '⚖️ Pháp Lý & Hợp Đồng' },
    { id: 'finance', label: '📊 Tài Chính & CFO' },
    { id: 'tech', label: '⚡ Kỹ Thuật & Code' },
    { id: 'management', label: '🎯 Quản Trị & Vận Hành' },
    { id: 'growth', label: '🌱 Phát Triển Cá Nhân' }
  ];

  const filteredPacks = packs.filter(p => {
    const matchTab = activeTab === 'catalog' ? true : p.installed;
    const matchCat = selectedCategory === 'all' || p.category === selectedCategory;
    const matchSearch = !searchQuery ||
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.author.toLowerCase().includes(searchQuery.toLowerCase());
    return matchTab && matchCat && matchSearch;
  });

  const installedCount = packs.filter(p => p.installed).length;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: '16px' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang nạp Sàn Giao Dịch Kỹ Năng (Skill-Pack Marketplace)...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
              🏪 Chợ Kỹ Năng & SOP Đóng Gói (Skill-Pack Marketplace)
            </h1>
            <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '12px', background: 'rgba(124, 58, 237, 0.15)', color: '#a855f7', border: '1px solid rgba(124, 58, 237, 0.3)' }}>
              Knowledge Economy
            </span>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '6px', marginBottom: 0 }}>
            Khám phá, trao đổi và cài đặt 1-click các bộ quy trình chuẩn (SOP), Prompt AI và tri thức chuyên gia vào Vault của bạn.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            className="btn btn--secondary"
            onClick={() => navigate('/operations')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <span>📁 Xem Operations Hub</span>
          </button>
          <button
            className="btn btn--primary"
            onClick={() => setShowPublishModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <span>📦 Đóng Gói & Rao Bán Pack</span>
          </button>
        </div>
      </div>

      {/* Tabs & Search Filter Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '20px',
        borderBottom: '1px solid var(--border)',
        paddingBottom: '16px'
      }}>
        {/* Navigation Tabs */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setActiveTab('catalog')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: activeTab === 'catalog' ? 'var(--accent-primary)' : 'rgba(255, 255, 255, 0.05)',
              color: activeTab === 'catalog' ? '#fff' : 'var(--text-muted)',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            🌐 Khám Phá Toàn Sàn ({packs.length})
          </button>
          <button
            onClick={() => setActiveTab('my_packs')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: activeTab === 'my_packs' ? 'var(--accent-primary)' : 'rgba(255, 255, 255, 0.05)',
              color: activeTab === 'my_packs' ? '#fff' : 'var(--text-muted)',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            💼 Tủ Đồ Đã Cài Đặt ({installedCount})
          </button>
        </div>

        {/* Search */}
        <div style={{ minWidth: '280px' }}>
          <input
            type="text"
            className="input"
            placeholder="🔍 Tìm kiếm kỹ năng, SOP, tác giả..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '100%', fontSize: '13px' }}
          />
        </div>
      </div>

      {/* Category Pills */}
      <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '16px', marginBottom: '20px' }}>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            style={{
              padding: '6px 14px',
              borderRadius: '20px',
              border: '1px solid',
              borderColor: selectedCategory === cat.id ? 'var(--accent-primary)' : 'rgba(255, 255, 255, 0.08)',
              background: selectedCategory === cat.id ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.02)',
              color: selectedCategory === cat.id ? '#c084fc' : 'var(--text-muted)',
              fontSize: '12px',
              fontWeight: selectedCategory === cat.id ? 600 : 400,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s'
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Grid of Skill-Packs */}
      {filteredPacks.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px dashed var(--border)',
          borderRadius: '16px'
        }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📦</div>
          <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
            Không tìm thấy Skill-Pack phù hợp
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', maxWidth: '400px', margin: '8px auto 20px' }}>
            {activeTab === 'my_packs'
              ? 'Bạn chưa cài đặt gói kỹ năng nào. Hãy khám phá trên Sàn và cài đặt với 1 cú click!'
              : 'Hãy thử đổi từ khóa tìm kiếm hoặc chọn danh mục khác.'}
          </p>
          {activeTab === 'my_packs' && (
            <button className="btn btn--primary" onClick={() => setActiveTab('catalog')}>
              Khám Phá Skill-Packs ➔
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '24px' }}>
          {filteredPacks.map(pack => (
            <div
              key={pack.id}
              style={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: pack.installed ? '1px solid rgba(52, 211, 153, 0.3)' : '1px solid var(--border)',
                borderRadius: '16px',
                padding: '22px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'all 0.2s',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {pack.installed && (
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '-32px',
                  transform: 'rotate(45deg)',
                  background: 'rgba(16, 185, 129, 0.85)',
                  color: '#fff',
                  fontSize: '9px',
                  fontWeight: '700',
                  padding: '2px 30px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Đã Cài Đặt
                </div>
              )}

              <div>
                {/* Badge & Price Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{
                    fontSize: '11px',
                    padding: '3px 8px',
                    borderRadius: '6px',
                    background: 'rgba(255, 255, 255, 0.06)',
                    color: 'var(--text-primary)',
                    fontWeight: 600
                  }}>
                    {pack.badge}
                  </span>

                  <span style={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: pack.price === 0 ? '#34d399' : '#fbbf24'
                  }}>
                    {pack.price_label}
                  </span>
                </div>

                {/* Title */}
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', lineHeight: 1.4, margin: '0 0 8px 0' }}>
                  {pack.title}
                </h3>

                {/* Author & Stats */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '11px', color: 'var(--text-muted)', marginBottom: '14px' }}>
                  <span>✍️ {pack.author}</span>
                  <span>⭐ {pack.rating.toFixed(1)} ({pack.reviews_count})</span>
                  <span>📥 {pack.installs_count} cài</span>
                </div>

                {/* Description */}
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 16px 0' }}>
                  {pack.description}
                </p>

                {/* Artifacts list */}
                <div style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '10px',
                  padding: '10px 12px',
                  marginBottom: '18px',
                  border: '1px solid rgba(255, 255, 255, 0.04)'
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase' }}>
                    Tài nguyên đóng gói ({pack.artifacts.length} files)
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {pack.artifacts.map((art, idx) => (
                      <div key={idx} style={{ fontSize: '12px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>📄</span>
                        <code style={{ fontSize: '11px', color: '#38bdf8' }}>{art}</code>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
                {pack.installed ? (
                  <button
                    className="btn btn--secondary"
                    onClick={() => navigate('/operations')}
                    style={{ width: '100%', borderColor: 'rgba(52, 211, 153, 0.4)', color: '#34d399', fontSize: '13px' }}
                  >
                    ✓ Đã Kích Hoạt Trong Operations Hub ➔
                  </button>
                ) : (
                  <button
                    className="btn btn--primary"
                    disabled={installingId === pack.id}
                    onClick={() => handleInstall(pack)}
                    style={{ width: '100%', fontSize: '13px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
                  >
                    {installingId === pack.id ? (
                      <>
                        <div className="spinner" style={{ width: 14, height: 14 }} />
                        <span>Đang cài đặt...</span>
                      </>
                    ) : (
                      <>
                        <span>⚡ Cài Đặt 1-Click Vào Vault</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: Publish / Rao bán Pack */}
      {showPublishModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.75)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '560px',
            padding: '28px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>
                📦 Đóng Gói & Rao Bán Skill-Pack
              </div>
              <button
                onClick={() => setShowPublishModal(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '18px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handlePublish} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                  Tiêu Đề Gói Kỹ Năng / SOP
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="VD: Senior System Architect SOP & Microservice Checklist"
                  value={publishForm.title}
                  onChange={e => setPublishForm({ ...publishForm, title: e.target.value })}
                  style={{ width: '100%' }}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                    Danh Mục
                  </label>
                  <select
                    className="input"
                    value={publishForm.category}
                    onChange={e => {
                      const cat = e.target.value as SkillPack['category'];
                      const badgeMap: Record<string, string> = {
                        legal: '⚖️ Pháp Lý',
                        finance: '📊 Tài Chính',
                        tech: '⚡ Kỹ Thuật',
                        management: '🎯 Vận Hành',
                        growth: '🌱 Phát Triển'
                      };
                      setPublishForm({ ...publishForm, category: cat, badge: badgeMap[cat] || '📦 Kỹ Năng' });
                    }}
                    style={{ width: '100%' }}
                  >
                    <option value="tech">⚡ Kỹ Thuật & Code</option>
                    <option value="legal">⚖️ Pháp Lý & Hợp Đồng</option>
                    <option value="finance">📊 Tài Chính & CFO</option>
                    <option value="management">🎯 Quản Trị & Vận Hành</option>
                    <option value="growth">🌱 Phát Triển Cá Nhân</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                    Giá Bán (Erix Credits, 0 = Miễn phí)
                  </label>
                  <input
                    type="number"
                    className="input"
                    min="0"
                    step="50"
                    placeholder="0"
                    value={publishForm.price}
                    onChange={e => setPublishForm({ ...publishForm, price: Math.max(0, parseInt(e.target.value) || 0) })}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                  Mô Tả Giá Trị Gói Tri Thức
                </label>
                <textarea
                  className="input"
                  rows={3}
                  placeholder="Mô tả các vấn đề gói SOP này giải quyết, đối tượng phù hợp và cách áp dụng..."
                  value={publishForm.description}
                  onChange={e => setPublishForm({ ...publishForm, description: e.target.value })}
                  style={{ width: '100%', resize: 'vertical' }}
                  required
                />
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                  Danh Sách Tệp Đóng Gói (Mỗi dòng 1 tên tệp .md)
                </label>
                <textarea
                  className="input"
                  rows={3}
                  value={publishForm.artifacts}
                  onChange={e => setPublishForm({ ...publishForm, artifacts: e.target.value })}
                  style={{ width: '100%', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }}
                />
              </div>

              {/* Privacy Guard Notice */}
              <div style={{
                padding: '12px',
                borderRadius: '8px',
                background: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <input
                  type="checkbox"
                  id="privacy_guard_consent"
                  checked={publishForm.privacy_clean_consent}
                  onChange={e => setPublishForm({ ...publishForm, privacy_clean_consent: e.target.checked })}
                  style={{ marginTop: '3px' }}
                />
                <label htmlFor="privacy_guard_consent" style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.4, cursor: 'pointer' }}>
                  🛡️ <strong>Tự động quét Privacy Guard:</strong> Hệ thống sẽ tự động quét và lọc sạch các thông tin nhạy cảm (số tài khoản, mật khẩu, CCCD, API token) trong toàn bộ tài nguyên trước khi công khai.
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => setShowPublishModal(false)}
                  disabled={publishing}
                >
                  Hủy Bỏ
                </button>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={publishing}
                >
                  {publishing ? 'Đang Đóng Gói...' : '🚀 Phát Hành Lên Sàn'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
